import React from "react";
import styled from "styled-components";
import {
  CheckmarkIcon,
  colors,
  fontFamilies,
  WarningIcon,
} from "@commonsku/styles";
import {
  getPodValidationStatus,
  POD_VALIDATION_STATUS,
} from "./podValidation";

const SETUP_BACKGROUND = "#E6EFF2";
const READY_BACKGROUND = "#F2FFF9";
const READY_COLOR = "#00733F";

interface PodValidationStatusProps {
  item: {
    item_id: string;
    pod_shop_item_validation?: any;
  };
  isPodShop?: boolean;
  onEditItem?: (itemId: string) => void;
}

const StatusPill = styled.span<{
  $background: string;
  $color: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  border-radius: 10000px;
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
  font-family: ${fontFamilies.regular};
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
`;

const UnresolvedWarning = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 10px;
  padding: 8px 12px;
  border-radius: 5px;
  background: ${colors.secondary2["20"]};
  color: ${colors.secondary2["90"]};
  font-family: ${fontFamilies.regular};
  font-size: 16px;
  line-height: 24px;
`;

const EditProductButton = styled.button`
  border: 0;
  border-radius: 4px;
  padding: 6px 12px;
  background: ${colors.primary1["70"]};
  color: ${colors.white};
  cursor: pointer;
  font-family: ${fontFamilies.demibold};
  font-size: 10px;
  line-height: 16px;
  white-space: nowrap;
`;

export function PodValidationStatus({
  item,
  isPodShop,
  onEditItem,
}: PodValidationStatusProps) {
  if (!isPodShop) {
    return null;
  }

  const status = getPodValidationStatus(item);

  if (status === POD_VALIDATION_STATUS.READY) {
    return (
      <StatusPill
        $background={READY_BACKGROUND}
        $color={READY_COLOR}
      >
        <CheckmarkIcon size="small" color={READY_COLOR} />
        Ready for PoD
      </StatusPill>
    );
  }

  if (status === POD_VALIDATION_STATUS.INVALID) {
    const handleEditProduct = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onEditItem?.(item.item_id);
    };

    return (
      <UnresolvedWarning>
        <WarningIcon color={colors.secondary2["70"]} />
        <span>This product has unresolved issues</span>
        <EditProductButton onClick={handleEditProduct}>
          Edit product
        </EditProductButton>
      </UnresolvedWarning>
    );
  }

  return (
    <StatusPill
      $background={SETUP_BACKGROUND}
      $color={colors.neutrals["90"]}
    >
      Setup Required
    </StatusPill>
  );
}
